import React, { useState } from 'react';

/**
 * Terminal/Command Prompt Style - Themed to match dashboard
 */
const TerminalProgress = ({ currentStep = 0, stepDetails = {}, isFullyCompleted = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const steps = [
    { id: 1, name: 'table_selection', label: 'Table Selection' },
    { id: 2, name: 'business_context', label: 'Business Context' },
    { id: 3, name: 'domain_classification', label: 'Domain Classification' },
    { id: 4, name: 'data_profiling', label: 'Data Profiling' },
    { id: 5, name: 'relationship_identification', label: 'Relationship Identification' },
    { id: 6, name: 'business_glossary', label: 'Business Glossary' },
  ];

  const currentStepInfo = steps[currentStep - 1];
  
  // Check if all steps are completed - use prop instead of just checking step number
  const isCompleted = isFullyCompleted;

  // Get color for each step - using specified colors
  const getStepColor = (stepId) => {
    const colors = {
      1: '#60A5FA',  // Blue-400
      2: '#38BDF8',  // Sky-400 (light blue)
      3: '#22D3EE',  // Cyan-400 (cyan blue)
      4: '#2DD4BF',  // Teal-400 (teal blue)
      5: '#818CF8',  // Indigo-400 (purple blue)
      6: '#10B981',  // Green-500 (completed)
    };
    return colors[stepId] || colors[1];
  };

  const currentColor = getStepColor(currentStep);

  // Get detail for a specific step
  const getStepDetail = (stepId) => {
    return stepDetails[stepId] || '';
  };

  return (
    <div
      className="absolute top-8 left-8 z-50 transition-all duration-300 font-mono text-xs flex items-start"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Compact View */}
      {!isExpanded && (
        <div 
          className={`flex h-9 items-center rounded-lg px-3 shadow-lg backdrop-blur-sm ${
            isCompleted 
              ? 'bg-emerald-900/15 border border-emerald-500/20 shadow-emerald-500/10'
              : 'bg-slate-900/90 border border-blue-500/20 shadow-blue-500/10'
          }`}
        >
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <>
                <span className="text-emerald-400">✓</span>
                <span className="text-emerald-400 font-semibold">Semantic Data Graph</span>
              </>
            ) : (
              <>
                <span style={{ color: currentColor }}>▸</span>
                <span style={{ color: currentColor }}>
                  [{currentStep}/6] {currentStepInfo?.name}
                </span>
                {/* Animated dots */}
                <span className="inline-flex gap-0.5" style={{ color: currentColor }}>
                  <span style={{ animation: 'blink 1.4s infinite' }}>.</span>
                  <span style={{ animation: 'blink 1.4s infinite 0.2s' }}>.</span>
                  <span style={{ animation: 'blink 1.4s infinite 0.4s' }}>.</span>
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div 
          className="bg-slate-900/90 border border-blue-500/20 rounded-lg px-4 py-3 min-w-[340px] backdrop-blur-sm"
          style={{
            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.15)'
          }}
        >
          {/* Terminal Header */}
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-blue-500/20">
            <span className="text-blue-400">~/semantic_graph_generator</span>
          </div>

          {/* Progress Bar */}
          <div className="mb-3 flex items-center gap-2">
            <div className="flex-1">
              <div className="h-1 bg-slate-800 rounded overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    isCompleted 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-400'
                      : 'bg-gradient-to-r from-blue-500 to-cyan-400'
                  }`}
                  style={{ width: `${((currentStep - 1) / 5) * 100}%` }}
                />
              </div>
            </div>
            <span className={isCompleted ? 'text-emerald-400' : 'text-cyan-400'}>
              {Math.round(((currentStep - 1) / 5) * 100)}%
            </span>
          </div>

          {/* Steps List */}
          <div className="space-y-2">
            {steps.map((step) => {
              // When isCompleted is true, all steps including step 6 should be complete
              const isComplete = isCompleted ? step.id <= 6 : step.id < currentStep;
              const isCurrent = !isCompleted && step.id === currentStep;
              const isPending = step.id > currentStep;
              const detail = getStepDetail(step.id);

              return (
                <div key={step.id} className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    {isComplete && (
                      <>
                        <span className="text-emerald-400">✓</span>
                        <span className="text-emerald-400/60 line-through flex-1">{step.name}</span>
                        <span className="text-emerald-400/40 text-[10px]">done</span>
                      </>
                    )}
                    {isCurrent && (
                      <>
                        <span className="text-blue-400 animate-pulse">▸</span>
                        <span className="text-cyan-400 font-semibold flex-1">{step.name}</span>
                        <span className="text-cyan-400 text-[10px] animate-pulse">running...</span>
                      </>
                    )}
                    {isPending && (
                      <>
                        <span className="text-gray-600">○</span>
                        <span className="text-gray-600 flex-1">{step.name}</span>
                        <span className="text-gray-600/60 text-[10px]">pending</span>
                      </>
                    )}
                  </div>
                  {/* Step Details */}
                  {detail && (isComplete || isCurrent) && (
                    <div className="ml-5 text-[10px] text-blue-400/50">
                      {detail}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Blinking Cursor */}
          <div className="mt-3 pt-2 border-t border-blue-500/20 flex items-center gap-2">
            <span className="text-cyan-400 animate-pulse">_</span>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes blink {
          0%, 20% {
            opacity: 0;
          }
          40%, 100% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default TerminalProgress;